import { Button, Col, Modal, Row } from "antd";
import { SIZE_MAPPING_OPTION } from "../../../../applications/constants/AppConst";
import { OrderItemMappingRequest } from "../../../../infrastructure/model/OrderModel";
import usePageState from "../../../hooks/usePageState";
import { useMemo, useState } from "react";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { useItemsContext } from "../../../context/AppItemsContext";
import { getItemInOrderUseCase } from "../../../../applications/useCase/ApiUseCase/OrderApiUseCase";

interface Props {
  setOpenPreviewHandle: (val: number | undefined) => void;
  openPreviewHandle: number | undefined;
}

function PreviewOrderTotalDialog(props: Props) {
  const { openPreviewHandle, setOpenPreviewHandle } = props;

  const { setLoading, showError } = usePageState();

  const { items } = useItemsContext();
  const [dataTable, setDataTable] = useState<OrderItemMappingRequest[]>([]);

  async function getItemsInOrder(orderId: number) {
    const res = await getItemInOrderUseCase({
      orderId,
      setLoading,
      showError,
    });
    if (res) {
      setDataTable(res);
    }
  }

  useAsyncEffect(async () => {
    if (openPreviewHandle === undefined) return;
    await getItemsInOrder(openPreviewHandle);
  }, [openPreviewHandle]);

  const groupedData = useMemo(() => {
    const result: Record<
      string, // item name
      Record<
        "S" | "M" | "L",
        { item_id: number; size: string; total_quantity: number; item_price: number }[]
      >
    > = {};

    dataTable.forEach((orderItem) => {
      const item = items.find((i) => i.id === orderItem.item_id);
      if (!item) return;

      const itemName = item.name;

      const size = SIZE_MAPPING_OPTION.find((s) => s.value === orderItem.item_size)?.label as
        | "S"
        | "M"
        | "L"
        | undefined;

      if (!size || !["S", "M", "L"].includes(size)) return;

      // Khởi tạo item
      if (!result[itemName]) {
        result[itemName] = { S: [], M: [], L: [] };
      }

      // Tìm xem size này đã có trong mảng chưa → nếu có thì cộng quantity
      const existing = result[itemName][size].find((x) => x.item_id === orderItem.item_id);

      if (existing) {
        existing.total_quantity += orderItem.item_quantity;
      } else {
        result[itemName][size].push({
          item_id: orderItem.item_id,
          size: size,
          total_quantity: orderItem.item_quantity,
          item_price: orderItem.item_price || 0,
        });
      }
    });

    return result;
  }, [dataTable, items]);

  return (
    <Modal
      title="Xem tổng hợp đơn hàng"
      open={openPreviewHandle != undefined}
      onCancel={() => setOpenPreviewHandle(undefined)}
      width={"80%"}
      footer={[
        <Button
          key="close"
          color="danger"
          variant="solid"
          onClick={() => setOpenPreviewHandle(undefined)}
        >
          Đóng
        </Button>,
      ]}
    >
      <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Thông tin chung */}
        <div style={{ marginBottom: 24, padding: 16, background: "#f5f5f5", borderRadius: 8 }}>
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <strong>Tổng số món:</strong> {dataTable.length}
            </Col>
            <Col span={12}>
              <strong>Tổng số lượng:</strong>{" "}
              {dataTable.reduce((sum, item) => sum + item.item_quantity, 0)}
            </Col>
          </Row>
        </div>

        {/* //TODO */}

        <Row gutter={16}>
          {Object.keys(groupedData)
            .sort() // sắp xếp tên món alphabet
            .map((itemName) => (
              <Col key={itemName} xs={24} sm={24} md={12} lg={8} xl={8}>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 8,
                    padding: 16,
                    border: "1px solid #ddd",
                    minHeight: 200,
                  }}
                >
                  <h2
                    style={{
                      textAlign: "center",
                      marginBottom: 16,
                      fontWeight: 700,
                    }}
                  >
                    {itemName}
                  </h2>

                  {/* Hiển thị size theo thứ tự S → M → L */}
                  {(["S", "M", "L"] as const).map((size) => {
                    const items = groupedData[itemName][size];
                    if (!items || items.length === 0) return null;

                    return (
                      <div
                        key={size}
                        style={{
                          background: "#fafafa",
                          marginBottom: 16,
                          borderRadius: 6,
                          padding: 12,
                        }}
                      >
                        <h3
                          style={{
                            fontWeight: 600,
                            marginBottom: 8,
                            color: size === "S" ? "#ff4d4f" : size === "M" ? "#fa8c16" : "#52c41a",
                          }}
                        >
                          Size {size}
                        </h3>

                        {items.map((item) => (
                          <Row
                            key={item.item_id + "-" + size}
                            style={{
                              padding: "6px 0",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            <Col span={20}>Số lượng</Col>
                            <Col span={4}>{item.total_quantity}</Col>
                          </Row>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </Col>
            ))}
        </Row>
      </div>
    </Modal>
  );
}

export default PreviewOrderTotalDialog;
